import React, { useState, useEffect, useCallback, useMemo, useRef, Fragment } from 'react';
import { pick } from 'lodash-es';
import { useDispatch, useSelector } from 'react-redux';

import ExportsTable from './ExportsTable';
import ExportActionsDropdown from './ExportActionsDropdown';
import CollapsibleSearch from '../CollapsibleSearch';
import BulkDeleteModal from '../Modals/BulkDeleteModal';
import Skeleton from '../../../../../components/Skeleton';
import UpsertExportModal from '../Modals/UpsertExportModal';
import DeleteExportModal from '../Modals/DeleteExportModal';
import ExportFailedModal from '../Modals/ExportFailedModal';
import TablePerPageDropdown from '../../TablePerPageDropdown';
import Button from '../../../../../components/Buttons/Button';
import Pagination from '../../../../../components/Pagination';
import FilterPills from '../../../../../components/FilterPills';
import SimpleCard from '../../../../../components/Card/SimpleCard';
import ErrorStateWithReload from '../../../../../components/ErrorStateWithReload';
import { ProcessingToastContent } from '../../../../../components/AdvancedExportModal/components/ProcessingToastContent';
import ScheduleExportModal from '../../../../../components/AdvancedExportModal/components/Modals/ScheduleExportModal';
import UnScheduleExportModal from '../../../../../components/AdvancedExportModal/components/Modals/UnScheduleExportModal';
import useScheduledExportsCount from '../../../../../components/AdvancedExportModal/hook/useScheduledExportsCount';
import { toast } from '../../../../../components/Toasts';
import { useAppCable } from '../../../../../hooks';
import api from '../../../api';
import {
  fetchCustomExportsData,
  updateCustomExportsData,
  updateCustomExportProgress,
  updateCustomExportsQueuePositions,
  patchCustomExportState,
  selectCustomExportsData,
  changeCustomExportsPerPage,
} from '../../../actions';
import {
  customExportsSettingsSelector,
  customExportsFiltersSelector,
  customExportsReportsSelector,
  selectedCustomExportsSelector,
} from '../../../selectors';
import { showProcessingNotify, processingExportName } from '../../../configs/custom-exports-config';
import { redirectToCustomReports } from '../../../utils/custom-exports-utils';
import { initParams } from '../../../configs';

const CustomExports = () => {
  const dispatch = useDispatch();

  const settings = useSelector(customExportsSettingsSelector);
  const filters = useSelector(customExportsFiltersSelector);
  const tableData = useSelector(customExportsReportsSelector);
  const selectedExports = useSelector(selectedCustomExportsSelector);

  const activeActionRef = useRef();
  const allExportsTotalRef = useRef(filters.pagination.total);

  // Memoized params
  const memoFilters = useRef();
  memoFilters.current = filters;
  const memoTableData = useRef();
  memoTableData.current = tableData;

  const [activeExport, setActiveExport] = useState(null);
  const [isMultipleDownloading, setMultipleDownloading] = useState(false);

  const [isUpsertModalOpen, setUpsertModalOpen] = useState(false);
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isBulkDeleteModalOpen, setBulkDeleteModalOpen] = useState(false);
  const [isScheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [isUnScheduleModalOpen, setUnScheduleModalOpen] = useState(false);
  const [showExportFailed, setExportFailed] = useState(false);

  const {
    scheduledExportsCount,
    scheduleLimitReached,
    isScheduledCountLoaded,
    refetchScheduledExportsCount,
  } = useScheduledExportsCount();

  const isFilteredExportsTypeAll = filters.only_scheduled === 'false';

  const resetPagination = useCallback(
    () => ({
      pagination: { ...memoFilters.current.pagination, current_page: 0 },
    }),
    []
  );

  const closeExportFailed = useCallback(() => {
    setExportFailed(false);
  }, []);

  const handleExportsListUpdate = useCallback((filtersParams = {}) => {
    const updatedFilters = pick(filtersParams, [
      'pagination',
      'query',
      'only_scheduled',
      'sort_by',
      'sort_order',
    ]);
    dispatch(fetchCustomExportsData(updatedFilters));
  }, []);

  const handleSearchChange = useCallback((query) => {
    handleExportsListUpdate({ query, ...resetPagination() });
  }, []);

  const handleExportTypeChange = useCallback((value) => {
    handleExportsListUpdate({ only_scheduled: value, ...resetPagination() });
    dispatch(selectCustomExportsData());
  }, []);

  const handleUpdateSelectedExports = () => {
    if (!selectedExports.length) return;
    dispatch(updateCustomExportsData());
  };

  const handleMultipleDownload = useCallback(() => {
    if (!selectedExports.length) return;

    setMultipleDownloading(true);
    api.customExports.downloadCustomExports({ custom_exports: selectedExports }).then(() => {
      toast.success(
        'Your request to download multiple exports has been received. We will notify you when the download is ready.'
      );
    });
  }, [selectedExports]);

  const closeUpsertModal = useCallback(() => {
    activeActionRef.current = undefined;
    setUpsertModalOpen(false);
    setActiveExport(null);
  }, []);

  const closeDeleteModal = useCallback(() => {
    setDeleteModalOpen(false);
    setActiveExport(null);
  }, []);

  const closeBulkDeleteModal = useCallback(() => {
    setBulkDeleteModalOpen(false);
  }, []);

  const closeScheduleModal = useCallback(() => {
    setScheduleModalOpen(false);
    setActiveExport(null);
  }, []);

  const closeUnScheduleModal = useCallback(() => {
    setUnScheduleModalOpen(false);
    setActiveExport(null);
  }, []);

  const onExportCreate = () => {
    redirectToCustomReports('/new');
  };

  const onExportEdit = useCallback((reportToEdit) => {
    if (reportToEdit.state !== 'pending') {
      activeActionRef.current = 'update';
      redirectToCustomReports(`/${reportToEdit.id}/edit`);
    }
  }, []);

  const onBulkDelete = useCallback(() => {
    setBulkDeleteModalOpen(true);
  }, []);

  const handleSortChange = useCallback(({ sortBy, sortOrder }) => {
    handleExportsListUpdate({
      sort_order: sortOrder,
      sort_by: sortBy,
      ...resetPagination(),
    });
    dispatch(selectCustomExportsData());
  }, []);

  const handleSelectedExportsChange = useCallback((exports) => {
    dispatch(selectCustomExportsData(exports));
  }, []);

  const handlePageChange = useCallback(({ selected }) => {
    handleExportsListUpdate({
      pagination: { ...memoFilters.current.pagination, current_page: selected },
    });
    dispatch(selectCustomExportsData());
  }, []);

  const handlePerPageChange = useCallback((value) => {
    dispatch(changeCustomExportsPerPage(value));
  }, []);

  const handleBackToEdit = useCallback(() => {
    onExportEdit(activeExport);
  }, [onExportEdit, activeExport]);

  const handleScheduledSuccess = useCallback(() => {
    closeScheduleModal();
    handleExportsListUpdate();
    refetchScheduledExportsCount();
  }, []);

  const handleUnScheduledSuccess = useCallback(() => {
    closeUnScheduleModal();
    handleExportsListUpdate();
    refetchScheduledExportsCount();
  }, []);

  const handleBulkDeleteSuccess = useCallback(() => {
    handleExportsListUpdate();
    dispatch(selectCustomExportsData());
  }, []);

  const receivedChannelData = useCallback((data) => {
    const { type } = data;

    if (type === 'state_transition_completed') {
      // sort_by values whose ordering can be invalidated by a state transition
      const reorderingSortKeys = ['updated_at'];
      if (reorderingSortKeys.includes(memoFilters.current.sort_by)) {
        handleExportsListUpdate(memoFilters.current);
        return;
      }

      const isVisible =
        data.custom_export && memoTableData.current.some(({ id }) => id === data.custom_export.id);
      if (isVisible) {
        dispatch(patchCustomExportState(data.custom_export));
      }
      return;
    }

    if (type === 'progress_update') {
      dispatch(
        updateCustomExportProgress({
          id: data.custom_export_id,
          progress: data.progress,
        })
      );
      return;
    }

    if (type === 'queue_positions_update') {
      dispatch(updateCustomExportsQueuePositions({ queuePositions: data.queue_positions }));
      return;
    }

    if (type === 'multiple_exports_download_ready') {
      setMultipleDownloading(false);
    }

    if (type === 'big_report_error') {
      setExportFailed(true);
      setActiveExport(data.custom_export.id);
    }
  }, []);

  useAppCable(
    { channel: 'CustomExportsChannel' },
    {
      onReceived: receivedChannelData,
    }
  );

  // Effects
  useEffect(() => {
    if (showProcessingNotify) {
      toast.success(<ProcessingToastContent name={processingExportName} allowSchedulingProgress />);
      // Strip the one-shot notify params so a page refresh doesn't re-trigger the toast.
      const searchParams = new URLSearchParams(window.location.search);
      searchParams.delete('export_generating_notify');
      searchParams.delete('export_name');
      const query = searchParams.toString();
      const newUrl = `${window.location.pathname}${query ? `?${query}` : ''}${
        window.location.hash
      }`;
      window.history.replaceState(null, '', newUrl);
    }
  }, []);

  useEffect(() => {
    if (initParams.permissions.allowCustomExports && !tableData.length) {
      dispatch(fetchCustomExportsData());
    }
  }, []);

  // Keep track of total count for all exports to properly display it in the pill
  useEffect(() => {
    if (isFilteredExportsTypeAll && !settings.pending) {
      allExportsTotalRef.current = filters.pagination.total;
    }
  }, [settings.pending, isFilteredExportsTypeAll, filters.pagination.total]);

  const tableActions = useMemo(
    () => ({
      onExportSelect(checked, item) {
        dispatch(
          selectCustomExportsData((prevState) =>
            checked ? [...prevState, item] : prevState.filter(({ id }) => id !== item.id)
          )
        );
      },
      onExportUpdate(item) {
        dispatch(updateCustomExportsData(item.id));
      },
      onExportEdit,
      onExportRename(item) {
        activeActionRef.current = 'update';
        setActiveExport(item);
        setUpsertModalOpen(true);
      },
      onExportDuplicate(item) {
        activeActionRef.current = 'duplicate';
        setActiveExport(item);
        setUpsertModalOpen(true);
      },
      onExportDelete(item) {
        setActiveExport(item);
        setDeleteModalOpen(true);
      },
      onSchedule(item) {
        setActiveExport(item);
        setScheduleModalOpen(true);
      },
      onScheduleUpdate(item) {
        setActiveExport(item);
        setScheduleModalOpen(true);
      },
      onScheduleCancel(item) {
        setActiveExport(item);
        setUnScheduleModalOpen(true);
      },
      scheduleLimitReached,
    }),
    [onExportEdit, scheduleLimitReached]
  );

  const filterPillsOptions = useMemo(() => {
    const getTitle = (baseTitle, count) => (count > 0 ? `${baseTitle} (${count})` : baseTitle);
    const renderSkeleton = (baseTitle) => (
      <span className="d-flex align-items-center gap-4">
        {baseTitle} <Skeleton width={16} height={16} />
      </span>
    );
    const allExportsTitle = getTitle(
      'All Exports',
      isFilteredExportsTypeAll ? filters.pagination.total : allExportsTotalRef.current
    );
    const scheduledExportsTitle = getTitle('Scheduled Exports', scheduledExportsCount);

    return [
      {
        label: settings.pending ? renderSkeleton('All Exports') : allExportsTitle,
        title: allExportsTitle,
        value: 'false',
      },
      {
        label: isScheduledCountLoaded ? scheduledExportsTitle : renderSkeleton('Scheduled Exports'),
        title: scheduledExportsTitle,
        value: 'true',
      },
    ];
  }, [
    settings,
    filters.pagination,
    isFilteredExportsTypeAll,
    isScheduledCountLoaded,
    scheduledExportsCount,
  ]);

  // Pagination
  const total = parseInt(filters.pagination.total, 10);
  const perPage = parseInt(filters.pagination.per_page, 10);
  const currentPage = parseInt(filters.pagination.current_page, 10);
  const showPagination = total > tableData.length;

  if (!initParams.permissions.allowCustomExports) return null;

  if (settings.error) return <ErrorStateWithReload />;

  return (
    <Fragment>
      <SimpleCard
        headerLeft={
          <FilterPills
            active={filters.only_scheduled}
            options={filterPillsOptions}
            onChange={handleExportTypeChange}
          />
        }
        headerRight={
          <div className="d-flex gap-8 align-items-center">
            <Button onClick={onExportCreate}>Create Export</Button>
            <ExportActionsDropdown
              selectedCount={selectedExports.length}
              disabled={!selectedExports.length}
              isDownloading={isMultipleDownloading}
              onExportsUpdate={handleUpdateSelectedExports}
              onExportsDownload={handleMultipleDownload}
              onExportsDelete={onBulkDelete}
            />
            <CollapsibleSearch onChange={handleSearchChange} />
            <TablePerPageDropdown
              active={perPage}
              total={total}
              disabled={total === 0}
              onChange={handlePerPageChange}
            />
          </div>
        }
        contentDisableGutters
        actionsLeft={
          showPagination ? (
            <Pagination
              total={total}
              perPage={perPage}
              currentPage={currentPage}
              onPageChange={handlePageChange}
            />
          ) : null
        }
        className="my-exports-card"
      >
        <ExportsTable
          tableData={tableData}
          tableActions={tableActions}
          selectedExports={selectedExports}
          settings={settings}
          initialRowsLimit={perPage}
          filteredExportsType={isFilteredExportsTypeAll ? 'all' : 'scheduled'}
          onSelectedExportsChange={handleSelectedExportsChange}
          onSortChange={handleSortChange}
        />
      </SimpleCard>

      {isDeleteModalOpen && (
        <DeleteExportModal
          open={isDeleteModalOpen}
          report={activeExport}
          onDeleteSuccess={handleExportsListUpdate}
          onClose={closeDeleteModal}
        />
      )}

      {isBulkDeleteModalOpen && (
        <BulkDeleteModal
          open={isBulkDeleteModalOpen}
          selectedExports={selectedExports}
          onDeleteSuccess={handleBulkDeleteSuccess}
          onClose={closeBulkDeleteModal}
        />
      )}

      {isUpsertModalOpen && (
        <UpsertExportModal
          open={isUpsertModalOpen}
          report={activeExport}
          action={activeActionRef.current}
          onUpdateSuccess={handleExportsListUpdate}
          onClose={closeUpsertModal}
        />
      )}

      {isScheduleModalOpen && (
        <ScheduleExportModal
          open={isScheduleModalOpen}
          report={activeExport}
          onBackToEdit={handleBackToEdit}
          onClose={closeScheduleModal}
          onSuccess={handleScheduledSuccess}
        />
      )}

      {isUnScheduleModalOpen && (
        <UnScheduleExportModal
          open={isUnScheduleModalOpen}
          report={activeExport}
          onClose={closeUnScheduleModal}
          onSuccess={handleUnScheduledSuccess}
        />
      )}

      {showExportFailed && (
        <ExportFailedModal
          open={showExportFailed}
          report={activeExport}
          allowSchedulingProgress
          onUpdateSuccess={handleExportsListUpdate}
          onClose={closeExportFailed}
        />
      )}
    </Fragment>
  );
};

export default CustomExports;
